import { Point } from 'pixi.js';

/**
 * Clase para optimizar búsquedas espaciales mediante:
 * - Spatial Grid (indexación por posición)
 * - Viewport Culling (solo elementos visibles)
 * - Pre-cálculo de bounding boxes
 */
export class SpatialOptimizer {
  constructor(gridCellSize = 200) {
    this.gridCellSize = gridCellSize
    this.spatialGrid = new Map()
  }

  /**
   * Pre-calcula los bounding boxes de un elemento para optimizar las búsquedas
   * @param {Object} element - Elemento con propiedades picX, picY, picWidth, picHeight, picRadius, picType
   */
  preCalculateBounds(element) {
    if (element._boundsCached) return // Ya está cacheado

    if (element.picType === 'nodes') {
      // Para nodos (círculos) - usar la misma lógica que el código original
      element._minX = element.picX - element.picRadius
      element._maxX = element.picX + element.picRadius
      element._minY = -element.picY - element.picRadius
      element._maxY = -element.picY + element.picRadius
    } else {
      // Para rectángulos
      element._minX = element.picX
      element._maxX = element.picX + element.picWidth
      element._minY = element.picY
      element._maxY = element.picY + element.picHeight
    }

    element._boundsCached = true
  }

  /**
   * Obtiene los bounds del viewport visible en coordenadas del contenedor
   * @param {Object} viewport - Viewport de PixiJS
   * @param {Container} picsContainer - Contenedor de elementos
   * @param {Application} app - Aplicación de PixiJS
   * @returns {Object|null} Bounds del viewport o null si no está disponible
   */
  getViewportBounds(viewport, picsContainer, app) {
    if (!picsContainer) {
      return null
    }

    let viewportBounds = null

    // Intentar obtener bounds del viewport si está disponible
    if (viewport && typeof viewport.getBounds === 'function') {
      viewportBounds = viewport.getBounds()
    }

    // Si no hay viewport, usar los bounds del renderer como fallback
    if (!viewportBounds && app && app.renderer) {
      viewportBounds = {
        x: 0,
        y: 0,
        width: app.renderer.width,
        height: app.renderer.height
      }
    }

    if (!viewportBounds) return null

    // Convertir bounds del viewport a coordenadas locales del contenedor
    const topLeft = picsContainer.toLocal(new Point(viewportBounds.x, viewportBounds.y))
    const bottomRight = picsContainer.toLocal(
      new Point(viewportBounds.x + viewportBounds.width, viewportBounds.y + viewportBounds.height)
    )

    return {
      minX: Math.min(topLeft.x, bottomRight.x),
      maxX: Math.max(topLeft.x, bottomRight.x),
      minY: Math.min(topLeft.y, bottomRight.y),
      maxY: Math.max(topLeft.y, bottomRight.y)
    }
  }

  /**
   * Obtiene las claves de las celdas que intersectan con un área
   * @param {number} minX - Coordenada X mínima
   * @param {number} maxX - Coordenada X máxima
   * @param {number} minY - Coordenada Y mínima
   * @param {number} maxY - Coordenada Y máxima
   * @returns {Array<string>} Array de claves de celdas (formato "x,y")
   */
  getCellsForBounds(minX, maxX, minY, maxY) {
    const cells = new Set()

    const startCellX = Math.floor(minX / this.gridCellSize)
    const endCellX = Math.floor(maxX / this.gridCellSize)
    const startCellY = Math.floor(minY / this.gridCellSize)
    const endCellY = Math.floor(maxY / this.gridCellSize)

    for (let x = startCellX; x <= endCellX; x++) {
      for (let y = startCellY; y <= endCellY; y++) {
        cells.add(`${x},${y}`)
      }
    }

    return Array.from(cells)
  }

  /**
   * Construye el spatial grid para indexar elementos por posición
   * @param {Container} container - Contenedor con los elementos a indexar
   */
  buildSpatialGrid(container) {
    this.spatialGrid = new Map()

    if (!container || container.children.length === 0) {
      return
    }

    // Pre-calcular bounds de todos los elementos
    for (const el of container.children) {
      this.preCalculateBounds(el)

      // Obtener las celdas que intersecta este elemento
      const cells = this.getCellsForBounds(el._minX, el._maxX, el._minY, el._maxY)

      // Agregar el elemento a cada celda
      for (const cellKey of cells) {
        if (!this.spatialGrid.has(cellKey)) {
          this.spatialGrid.set(cellKey, [])
        }
        this.spatialGrid.get(cellKey).push(el)
      }
    }
  }

  /**
   * Obtiene elementos del spatial grid que podrían intersectar con un área
   * @param {number} minX - Coordenada X mínima del área
   * @param {number} maxX - Coordenada X máxima del área
   * @param {number} minY - Coordenada Y mínima del área
   * @param {number} maxY - Coordenada Y máxima del área
   * @param {Container} container - Contenedor de elementos (fallback si no hay grid)
   * @returns {Array} Array de elementos candidatos
   */
  getElementsFromGrid(minX, maxX, minY, maxY, container) {
    if (!this.spatialGrid || this.spatialGrid.size === 0) {
      // Si no hay grid, retornar todos los elementos (fallback)
      return container ? Array.from(container.children) : []
    }

    const cells = this.getCellsForBounds(minX, maxX, minY, maxY)
    const candidates = new Set()

    for (const cellKey of cells) {
      const cellElements = this.spatialGrid.get(cellKey)
      if (cellElements) {
        for (const el of cellElements) {
          candidates.add(el)
        }
      }
    }

    return Array.from(candidates)
  }

  /**
   * Verifica si un elemento está visible en el viewport
   * @param {Object} element - Elemento a verificar
   * @param {Object} viewportBounds - Bounds del viewport
   * @returns {boolean} true si el elemento es visible
   */
  isElementVisible(element, viewportBounds) {
    if (!viewportBounds) return true // Si no hay viewport, asumir visible

    // Usar bounds pre-calculados
    if (!element._boundsCached) {
      this.preCalculateBounds(element)
    }

    return !(
      element._maxX < viewportBounds.minX ||
      element._minX > viewportBounds.maxX ||
      element._maxY < viewportBounds.minY ||
      element._minY > viewportBounds.maxY
    )
  }

  /**
   * Verifica si un elemento intersecta con un área de selección
   * @param {Object} element - Elemento a verificar
   * @param {Object} selectionStart - Punto inicial de selección {x, y}
   * @param {Object} selectionEnd - Punto final de selección {x, y}
   * @returns {boolean} true si el elemento intersecta
   */
  intersectsSelection(element, selectionStart, selectionEnd) {
    if (!element._boundsCached) {
      this.preCalculateBounds(element)
    }

    return !(
      element._maxX < selectionStart.x ||
      element._minX > selectionEnd.x ||
      element._maxY < selectionStart.y ||
      element._minY > selectionEnd.y
    )
  }

  /**
   * Invalida el cache de bounds de todos los elementos en el contenedor
   * @param {Container} container - Contenedor con elementos
   */
  invalidateCache(container) {
    if (container) {
      for (const el of container.children) {
        el._boundsCached = false
      }
    }
  }

  /**
   * Limpia el spatial grid y todas las referencias
   */
  clear() {
    // Limpiar referencias en cada celda antes de limpiar el Map
    for (const [key, elements] of this.spatialGrid) {
      elements.length = 0; // Vaciar el array de elementos
    }
    this.spatialGrid.clear();
    this.spatialGrid = null;
  }
}

